import React, { useState, useMemo } from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import "./VehicleOverviewSection.css";

export default function VehicleOverviewSection({ vehicle }) {
    const {
        brand,
        modelName,
        title,
        shortDescription,
        variants = [],
        colors = [],
        imageSrc,
        engine,
        payload,
        hpTorque
    } = vehicle;

    // Variant state
    const [selectedVariantId, setSelectedVariantId] = useState(
        variants[0]?.id || ""
    );

    const selectedVariant = useMemo(() => {
        if (!variants.length) return null;
        return variants.find((v) => v.id === selectedVariantId) || variants[0];
    }, [variants, selectedVariantId]);


    const displayHpTorque = useMemo(() => {
        return selectedVariant?.hpTorque || hpTorque || "";
    }, [selectedVariant, hpTorque]);

    const displayPayload = useMemo(() => {
        return selectedVariant?.payload || payload || "";
    }, [selectedVariant, payload]);

    const displayEngine = useMemo(() => {
        return selectedVariant?.engine || engine || "";
    }, [selectedVariant, engine]);


    // Color / image state
    const [activeColorId, setActiveColorId] = useState(colors[0]?.id || "");

    const activeColor = useMemo(() => {
        if (!colors.length) return null;
        return colors.find((c) => c.id === activeColorId) || colors[0];
    }, [colors, activeColorId]);

    const displayImageSrc = activeColor?.imageSrc || imageSrc;

    const brochureUrl =
        selectedVariant?.brochureUrl || vehicle.brochureUrl || "";

    return (
        <section className="vehicle-overview section-spacing">
            <Container fluid>
                <Row className="align-items-center g-4">
                    {/* CENTER on mobile/tablet, middle on desktop */}
                    <Col
                        xs={12}
                        lg={4}
                        className="order-1 order-lg-2 vehicle-overview__center"
                    >
                        <div className="vehicle-overview__image-wrapper">
                            {displayImageSrc && (
                                <img
                                    src={displayImageSrc}
                                    alt={title}
                                    className="vehicle-overview__image"
                                />
                            )}
                        </div>

                        {colors.length > 0 && (
                            <div className="vehicle-overview__colors">
                                <span className="vehicle-overview__colors-label">
                                    Available Colors:
                                </span>
                                <div className="vehicle-overview__swatches">
                                    {colors.map((color) => (
                                        <button
                                            key={color.id}
                                            type="button"
                                            className={`vehicle-overview__swatch${color.id === activeColorId
                                                ? " vehicle-overview__swatch--active"
                                                : ""
                                                }`}
                                            style={{ backgroundColor: color.colorCode }}
                                            onClick={() => setActiveColorId(color.id)}
                                            aria-label={color.name}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </Col>

                    {/* LEFT on desktop, second on mobile/tablet */}
                    <Col
                        xs={12}
                        lg={4}
                        className="order-2 order-lg-1 vehicle-overview__left"
                    >
                        <div className="vehicle-overview__brand">{brand}</div>
                        <h1 className="vehicle-overview__title">{modelName}</h1>

                        {variants.length > 0 && (
                            <div className="vehicle-overview__variants">
                                {/* <Form.Label className="vehicle-overview__variants-label">
                                    Select Variant
                                </Form.Label> */}
                                <Form.Select
                                    value={selectedVariant?.id || ""}
                                    onChange={(e) => setSelectedVariantId(e.target.value)}
                                    className="vehicle-overview__select form-select lk-brand-input mb-4"
                                >
                                    {variants.map((variant) => (
                                        <option key={variant.id} value={variant.id}>
                                            {variant.name}
                                        </option>
                                    ))}
                                </Form.Select>

                                {selectedVariant?.priceLabel && (
                                    <div className="vehicle-overview__price">
                                        {selectedVariant.priceLabel}
                                    </div>
                                )}

                                <h6 className="vat-highlight mb-4">Exclusive of VAT*</h6>
                            </div>
                        )}

                        {shortDescription && (
                            <p className="vehicle-overview__description">
                                {shortDescription}
                            </p>
                        )}

                        {brochureUrl && (
                            <a
                                href={brochureUrl}
                                className="lk-brand-btn-1"
                                download
                            >
                                Download Brochure
                            </a>
                        )}
                    </Col>

                    {/* RIGHT on all views, last on mobile/tablet */}
                    <Col
                        xs={12}
                        lg={4}
                        className="order-3 vehicle-overview__right"
                    >
                        <h2 className="vehicle-overview__highlights-title">
                            Overview
                        </h2>

                        <div className="vehicle-overview__highlights">
                            <div className="vehicle-overview__highlight-list">
                                {/* {engine && (
                                    <div className="vehicle-overview__highlight-item">
                                        <span className="vehicle-overview__highlight-label">
                                            Engine
                                        </span>
                                        <span className="vehicle-overview__highlight-value">
                                            {engine}
                                        </span>
                                    </div>
                                )} */}

                                {displayEngine && (
                                    <div className="vehicle-overview__highlight-item">
                                        <span className="vehicle-overview__highlight-label">
                                            Engine
                                        </span>
                                        <span className="vehicle-overview__highlight-value">
                                            {displayEngine}
                                        </span>
                                    </div>
                                )}

                            </div>
                        </div>

                        <div className="vehicle-overview__highlights">
                            <div className="vehicle-overview__highlight-list">
                                {/* {payload && (
                                    <div className="vehicle-overview__highlight-item">
                                        <span className="vehicle-overview__highlight-label">
                                            Payload
                                        </span>
                                        <span className="vehicle-overview__highlight-value">
                                            {payload}
                                        </span>
                                    </div>
                                )} */}


                                {displayPayload && (
                                    <div className="vehicle-overview__highlight-item">
                                        <span className="vehicle-overview__highlight-label">
                                            Payload
                                        </span>
                                        <span className="vehicle-overview__highlight-value">
                                            {displayPayload}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="vehicle-overview__highlights">
                            <div className="vehicle-overview__highlight-list">

                                {/* {hpTorque && (
                                    <div className="vehicle-overview__highlight-item">
                                        <span className="vehicle-overview__highlight-label">
                                            HP / Torque
                                        </span>
                                        <span className="vehicle-overview__highlight-value">
                                            {hpTorque}
                                        </span>
                                    </div>
                                )} */}


                                {displayHpTorque && (
                                    <div className="vehicle-overview__highlight-item">
                                        <span className="vehicle-overview__highlight-label">
                                            HP / Torque
                                        </span>
                                        <span className="vehicle-overview__highlight-value">
                                            {displayHpTorque}
                                        </span>
                                    </div>
                                )}

                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>
        </section >
    );
}
